/**
 * AccountFormWizard
 *
 * Multi-step wizard for creating new accounts.
 * Steps: Type → [Kind if Asset] → Identity → Settings → [Bank Details if bank subtype]
 *
 * Keyboard shortcuts (when focus is not inside an input):
 *  - Arrow keys : navigate between radio cards within a group
 *  - 1–5        : select account type  (type step)
 *  - 1–3        : select asset kind    (kind step)
 *  - Enter      : advance to next step / create
 *  - Escape / Alt+← : go back one step (or close on first step)
 */

import { useState, useMemo, useRef, useEffect } from 'react'

import { Modal } from '@elsapiens/ui'

import { canProceedGstStep, GstStep } from './AccountFormWizard-gststep'
import { BankDetailsStep, WizardFooter } from './AccountFormWizard-parts'
import { IdentityStep, SettingsStep, type AccountIntent } from './AccountFormWizard-steps'
import { KindStep, TypeStep, TYPE_SHORTCUT_MAP, KIND_VALUES } from './AccountFormWizard-typestep'
import { type AccountFormData } from './chart-of-accounts-helpers'
import { CurrencyConfirmDialog } from '../../components/ui'
import { isBankOnlySubtype } from '../../constants/labels'
import { validateAccountCode, checkReservedCode } from '../../constants/reserved-account-codes'

import type { Account, AccountType } from '../../services/account-service'

// ── Constants ──────────────────────────────────────────────────────────────────

const ENTITY_MANAGED_SUBTYPES = new Set(['Accounts Receivable', 'Accounts Payable', 'Customer Advances', 'Vendor Advances'])

type StepId = 'type' | 'kind' | 'identity' | 'settings' | 'bank' | 'gst'

// ── Props ──────────────────────────────────────────────────────────────────────

interface AccountFormWizardProps {
  isOpen: boolean
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  isSaving: boolean
  isLoadingBankDetails: boolean
  tagInput: string
  setTagInput: (v: string) => void
  parentOptions: Array<{ value: string; label: string }>
  currencies: Array<{ value: string; label: string }>
  accountsMap: Map<string, Account>
  isShowCurrencyConfirm: boolean
  onClose: () => void
  onSave: () => void
  onCurrencyConfirmClose: () => void
  onCurrencyConfirm: () => void
  baseCurrency: string
}

// ── Step navigation ────────────────────────────────────────────────────────────

function nextStepAfterType(accountType: AccountType, isGstMode: boolean): StepId {
  if (isGstMode) {return 'gst'}
  return accountType === 'ACCOUNT_TYPE_ASSET' ? 'kind' : 'identity'
}

function nextStepFor(current: StepId, accountType: AccountType, shouldShowBankStep: boolean, isGstMode: boolean): StepId | null {
  switch (current) {
    case 'type':     return nextStepAfterType(accountType, isGstMode)
    case 'kind':     return 'identity'
    case 'identity': return 'settings'
    case 'settings': return shouldShowBankStep ? 'bank' : null
    case 'bank':     return null
    case 'gst':      return null
  }
}

function prevStepFor(current: StepId, accountType: AccountType): StepId | null {
  switch (current) {
    case 'type':     return null
    case 'kind':     return 'type'
    case 'identity': return accountType === 'ACCOUNT_TYPE_ASSET' ? 'kind' : 'type'
    case 'settings': return 'identity'
    case 'bank':     return 'settings'
    case 'gst':      return 'type'
  }
}

interface CanProceedArgs {
  stepId: StepId
  formData: AccountFormData
  codeError: string | undefined
  isEntityControlParent: boolean
  accountsMap: Map<string, Account>
}

function canProceedBankStep(formData: AccountFormData): boolean {
  const hasBase = !!(formData.bankName.trim() && formData.accountNumber.trim())
  const isIfscNeeded = formData.currency === 'INR'
  return hasBase && (!isIfscNeeded || !!formData.ifscCode.trim())
}

function canProceedAtStep({ stepId, formData, codeError, isEntityControlParent, accountsMap }: CanProceedArgs): boolean {
  switch (stepId) {
    case 'type':     return true
    case 'kind':     return true
    case 'identity': return !!(formData.code && formData.name && !codeError && !isEntityControlParent)
    case 'settings': return !!formData.currency
    case 'bank':     return canProceedBankStep(formData)
    case 'gst':      return canProceedGstStep(formData, accountsMap)
  }
}

// ── Keyboard handling ──────────────────────────────────────────────────────────

function isDropdownLikeTarget(target: HTMLElement): boolean {
  return target.getAttribute('aria-haspopup') === 'listbox' || !!document.querySelector('[role="listbox"]')
}

function isPlainEnter(e: KeyboardEvent): boolean {
  return e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey
}

function shouldInterceptEnter(target: HTMLElement): boolean {
  if (target instanceof HTMLTextAreaElement) {return false}
  if (target instanceof HTMLSelectElement) {return false}
  if (target instanceof HTMLButtonElement && target.getAttribute('role') !== 'radio') {return false}
  return true
}

function isEditableTarget(target: HTMLElement): boolean {
  return target instanceof HTMLInputElement
    || target instanceof HTMLTextAreaElement
    || target instanceof HTMLSelectElement
}

function blurActiveEditableField(): boolean {
  const active = document.activeElement
  if (!isEditableTarget(active as HTMLElement)) {return false}
  (active as HTMLElement).blur()
  return true
}

interface KeyHandlerArgs {
  isOpen: boolean
  handleNextRef: React.MutableRefObject<() => void>
  handleBackRef: React.MutableRefObject<() => void>
  stepIdRef: React.MutableRefObject<StepId>
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  setIntent: (v: AccountIntent) => void
  setIsGstMode: (v: boolean) => void
}

function handleShortcutKeys(
  e: KeyboardEvent, sid: StepId,
  setFormData: KeyHandlerArgs['setFormData'], setIntent: KeyHandlerArgs['setIntent'], setIsGstMode: KeyHandlerArgs['setIsGstMode'],
): void {
  if (sid === 'type') {
    if (e.key === '6') {
      e.preventDefault()
      setIsGstMode(true)
      setFormData(prev => ({ ...prev, gstComponent: prev.gstComponent || 'cgst', gstDirection: prev.gstDirection || 'output' }))
      return
    }
    const t = TYPE_SHORTCUT_MAP[e.key]
    if (t) {
      e.preventDefault()
      setFormData(prev => ({
        ...prev, accountType: t, parentId: '', code: '', accountSubtype: '',
        gstComponent: '', gstDirection: '', gstRate: '', systemTag: '',
      }))
      setIntent('general')
      setIsGstMode(false)
    }
    return
  }
  if (sid === 'kind') {
    const kindIdx = parseInt(e.key) - 1
    if (kindIdx >= 0 && kindIdx < KIND_VALUES.length) {
      e.preventDefault()
      const k = KIND_VALUES[kindIdx]
      if (k) {setIntent(k)}
    }
  }
}

function useWizardKeyboard(args: KeyHandlerArgs): void {
  const { isOpen, handleNextRef, handleBackRef, stepIdRef, setFormData, setIntent, setIsGstMode } = args
  useEffect(() => {
    if (!isOpen) {return}

    function onKeyDown(e: KeyboardEvent): void {
      const target = e.target as HTMLElement
      if (isDropdownLikeTarget(target)) {return}

      if (isPlainEnter(e) && shouldInterceptEnter(target)) {
        e.preventDefault()
        handleNextRef.current()
        return
      }

      if (isEditableTarget(target)) {return}

      if (e.key === 'Escape') {
        if (blurActiveEditableField()) {
          e.preventDefault()
          e.stopPropagation()
        }
        return
      }

      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault()
        handleBackRef.current()
        return
      }

      handleShortcutKeys(e, stepIdRef.current, setFormData, setIntent, setIsGstMode)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, setFormData, setIntent, setIsGstMode, handleNextRef, handleBackRef, stepIdRef])
}

// ── Body ──────────────────────────────────────────────────────────────────────

interface WizardBodyProps {
  stepId: StepId
  intent: AccountIntent
  setIntent: (v: AccountIntent) => void
  isGstMode: boolean
  setIsGstMode: (v: boolean) => void
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  codeError: string | undefined
  codeWarning: string | undefined
  parentOptions: Array<{ value: string; label: string }>
  accountsMap: Map<string, Account>
  selectedParent: Account | undefined
  isEntityControlParent: boolean
  currencies: Array<{ value: string; label: string }>
  tagInput: string
  setTagInput: (v: string) => void
  isLoadingBankDetails: boolean
}

function WizardBody(p: WizardBodyProps): JSX.Element {
  return (
    <div className="flex flex-col el-gap-field min-h-[300px]">
      {p.stepId === 'type' && (
        <TypeStep formData={p.formData} isGstMode={p.isGstMode} setFormData={p.setFormData} setIntent={p.setIntent} setIsGstMode={p.setIsGstMode} />
      )}
      {p.stepId === 'kind' && <KindStep intent={p.intent} setIntent={p.setIntent} />}
      {p.stepId === 'identity' && (
        <IdentityStep
          accountsMap={p.accountsMap}
          codeError={p.codeError}
          codeWarning={p.codeWarning}
          formData={p.formData}
          intent={p.intent}
          isEntityControlParent={p.isEntityControlParent}
          parentOptions={p.parentOptions}
          selectedParent={p.selectedParent}
          setFormData={p.setFormData}
        />
      )}
      {p.stepId === 'settings' && (
        <SettingsStep
          currencies={p.currencies}
          formData={p.formData}
          intent={p.intent}
          setFormData={p.setFormData}
          setTagInput={p.setTagInput}
          tagInput={p.tagInput}
        />
      )}
      {p.stepId === 'bank' && (
        <BankDetailsStep
          formData={p.formData}
          isLoadingBankDetails={p.isLoadingBankDetails}
          setFormData={p.setFormData}
        />
      )}
      {p.stepId === 'gst' && (
        <GstStep accountsMap={p.accountsMap} formData={p.formData} setFormData={p.setFormData} />
      )}
    </div>
  )
}

// ── Cash/bank parent inference ────────────────────────────────────────────────

function useCashBankParent(accountsMap: Map<string, Account>, intent: AccountIntent, setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>): string {
  const cashBankParentId = useMemo(() => {
    const header = Array.from(accountsMap.values()).find(a =>
      a.accountType === 'ACCOUNT_TYPE_ASSET' &&
      a.isHeader &&
      (a.name.toLowerCase().includes('cash') || a.name.toLowerCase().includes('bank')),
    )
    return header?.id ?? ''
  }, [accountsMap])

  const prevIntentRef = useRef<AccountIntent>('general')
  useEffect(() => {
    const prev = prevIntentRef.current
    prevIntentRef.current = intent
    if ((intent === 'cash' || intent === 'bank') && prev !== intent) {
      setFormData(f => ({ ...f, parentId: f.parentId || cashBankParentId, isHeader: false }))
    }
  }, [intent, cashBankParentId, setFormData])

  return cashBankParentId
}

// ── Derived state + navigation hooks ──────────────────────────────────────────

interface DerivedWizardState {
  shouldShowBankStep: boolean
  codeError: string | undefined
  codeWarning: string | undefined
  selectedParent: Account | undefined
  isEntityControlParent: boolean
  isLastStep: boolean
  isProceedDisabled: boolean
}

interface DeriveWizardStateArgs {
  intent: AccountIntent
  stepId: StepId
  formData: AccountFormData
  accountsMap: Map<string, Account>
  isSaving: boolean
  isGstMode: boolean
}

function deriveWizardState({ intent, stepId, formData, accountsMap, isSaving, isGstMode }: DeriveWizardStateArgs): DerivedWizardState {
  const shouldShowBankStep = intent === 'bank' && isBankOnlySubtype(formData.accountSubtype)
  const codeFormatError = formData.code ? validateAccountCode(formData.code, formData.accountType) : undefined
  // Reserved-code mismatches are soft warnings in the wizard too — don't block progression,
  // but still surface the reason to the user (mirrors AccountForm's codeWarning).
  const codeError = codeFormatError
  const codeWarning = (!codeFormatError && formData.code)
    ? checkReservedCode(formData.code, formData.accountSubtype)
    : undefined
  const selectedParent = formData.parentId ? accountsMap.get(formData.parentId) : undefined
  const isEntityControlParent = !!(
    selectedParent?.isControl && ENTITY_MANAGED_SUBTYPES.has(selectedParent.accountSubtype ?? '')
  )
  const isLastStep = nextStepFor(stepId, formData.accountType, shouldShowBankStep, isGstMode) === null
  const isProceedDisabled = isSaving || !canProceedAtStep({ stepId, formData, codeError, isEntityControlParent, accountsMap })
  return { shouldShowBankStep, codeError, codeWarning, selectedParent, isEntityControlParent, isLastStep, isProceedDisabled }
}

function useWizardNavigation(args: {
  stepId: StepId
  setStepId: (id: StepId) => void
  formData: AccountFormData
  derived: DerivedWizardState
  accountsMap: Map<string, Account>
  isGstMode: boolean
  onSave: () => void
  onClose: () => void
}): { handleNext: () => void; handleBack: () => void } {
  const { stepId, setStepId, formData, derived, accountsMap, isGstMode, onSave, onClose } = args

  function handleNext(): void {
    if (!canProceedAtStep({ stepId, formData, codeError: derived.codeError, isEntityControlParent: derived.isEntityControlParent, accountsMap })) {return}
    if (derived.isLastStep) { onSave(); return }
    const next = nextStepFor(stepId, formData.accountType, derived.shouldShowBankStep, isGstMode)
    if (next) {setStepId(next)}
  }

  function handleBack(): void {
    const prev = prevStepFor(stepId, formData.accountType)
    if (prev) {setStepId(prev)}
    else {onClose()}
  }

  return { handleNext, handleBack }
}

// ── Wizard ─────────────────────────────────────────────────────────────────────

interface WizardModalContentProps {
  isOpen: boolean
  derived: DerivedWizardState
  stepId: StepId
  intent: AccountIntent
  setIntent: (v: AccountIntent) => void
  isGstMode: boolean
  setIsGstMode: (v: boolean) => void
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  parentOptions: Array<{ value: string; label: string }>
  accountsMap: Map<string, Account>
  currencies: Array<{ value: string; label: string }>
  tagInput: string
  setTagInput: (v: string) => void
  isLoadingBankDetails: boolean
  isSaving: boolean
  onClose: () => void
  handleNext: () => void
  handleBack: () => void
}

function buildBodyProps(a: WizardModalContentProps): WizardBodyProps {
  return {
    accountsMap: a.accountsMap,
    codeError: a.derived.codeError,
    codeWarning: a.derived.codeWarning,
    currencies: a.currencies,
    formData: a.formData,
    intent: a.intent,
    isEntityControlParent: a.derived.isEntityControlParent,
    isGstMode: a.isGstMode,
    isLoadingBankDetails: a.isLoadingBankDetails,
    parentOptions: a.parentOptions,
    selectedParent: a.derived.selectedParent,
    setFormData: a.setFormData,
    setIntent: a.setIntent,
    setIsGstMode: a.setIsGstMode,
    setTagInput: a.setTagInput,
    stepId: a.stepId,
    tagInput: a.tagInput,
  }
}

function WizardModalContent(args: WizardModalContentProps): JSX.Element {
  return (
    <Modal
      footer={(
        <WizardFooter
          canBack={prevStepFor(args.stepId, args.formData.accountType) !== null}
          isLastStep={args.derived.isLastStep}
          isProceedDisabled={args.derived.isProceedDisabled}
          isSaving={args.isSaving}
          onBack={args.handleBack}
          onCancel={args.onClose}
          onNext={args.handleNext}
        />
      )}
      isOpen={args.isOpen}
      size="md"
      title={args.isGstMode ? 'Create GST Ledger' : 'Create Account'}
      onClose={args.onClose}
    >
      <WizardBody {...buildBodyProps(args)} />
    </Modal>
  )
}

function useWizardState(props: AccountFormWizardProps): {
  stepId: StepId
  setStepId: (id: StepId) => void
  intent: AccountIntent
  setIntent: (v: AccountIntent) => void
  isGstMode: boolean
  setIsGstMode: (v: boolean) => void
  derived: DerivedWizardState
  handleNext: () => void
  handleBack: () => void
} {
  const { isOpen, formData, setFormData, isSaving, accountsMap, onSave, onClose } = props
  const [stepId, setStepId] = useState<StepId>('type')
  const [intent, setIntent] = useState<AccountIntent>('general')
  const [isGstMode, setIsGstMode] = useState(false)

  useEffect(() => {
    if (isOpen) { setStepId('type'); setIntent('general'); setIsGstMode(false) }
  }, [isOpen])

  useCashBankParent(accountsMap, intent, setFormData)
  const derived = deriveWizardState({ intent, stepId, formData, accountsMap, isSaving, isGstMode })
  const { handleNext, handleBack } = useWizardNavigation({
    stepId, setStepId, formData, derived, accountsMap, isGstMode, onSave, onClose,
  })

  const handleNextRef = useRef(handleNext); handleNextRef.current = handleNext
  const handleBackRef = useRef(handleBack); handleBackRef.current = handleBack
  const stepIdRef = useRef(stepId); stepIdRef.current = stepId

  useWizardKeyboard({ isOpen, handleNextRef, handleBackRef, stepIdRef, setFormData, setIntent, setIsGstMode })

  return { stepId, setStepId, intent, setIntent, isGstMode, setIsGstMode, derived, handleNext, handleBack }
}

export function AccountFormWizard(props: AccountFormWizardProps): JSX.Element {
  const {
    isOpen, formData, setFormData, isSaving, isLoadingBankDetails,
    tagInput, setTagInput, parentOptions, currencies, accountsMap,
    isShowCurrencyConfirm, onClose, onCurrencyConfirmClose, onCurrencyConfirm, baseCurrency,
  } = props
  const { stepId, intent, setIntent, isGstMode, setIsGstMode, derived, handleNext, handleBack } = useWizardState(props)

  return (
    <>
      <WizardModalContent
        accountsMap={accountsMap} currencies={currencies} derived={derived}
        formData={formData} handleBack={handleBack} handleNext={handleNext}
        intent={intent} isGstMode={isGstMode} isLoadingBankDetails={isLoadingBankDetails}
        isOpen={isOpen} isSaving={isSaving} parentOptions={parentOptions}
        setFormData={setFormData} setIntent={setIntent} setIsGstMode={setIsGstMode} setTagInput={setTagInput}
        stepId={stepId} tagInput={tagInput} onClose={onClose}
      />
      <CurrencyConfirmDialog
        currencyCode={formData.currency !== '' ? formData.currency : baseCurrency}
        entityLabel="Account"
        isLoading={isSaving}
        isOpen={isShowCurrencyConfirm}
        onClose={onCurrencyConfirmClose}
        onConfirm={onCurrencyConfirm}
      />
    </>
  )
}
